/**
 * Month-calendar arithmetic shared by every calendar view (the org hub's and
 * My Work's). Pure: dates in, cells and labels out, in the viewer's zone.
 */

/** Local calendar day as YYYY-MM-DD (the grid is in the viewer's zone). */
export function dayKey(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

export interface CalendarCell {
  key: string;
  date: Date;
  inMonth: boolean;
  isToday: boolean;
}

/**
 * Six Monday-first weeks covering `month` (0-based) of `year`. Always 42
 * cells so the grid never jumps height between months.
 */
export function monthGrid(year: number, month: number, today: Date): CalendarCell[] {
  const first = new Date(year, month, 1);
  const lead = (first.getDay() + 6) % 7; // Monday = 0
  const start = new Date(year, month, 1 - lead);
  const todayKey = dayKey(today);
  return Array.from({ length: 42 }, (_, i) => {
    const date = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
    const key = dayKey(date);
    return { key, date, inMonth: date.getMonth() === month, isToday: key === todayKey };
  });
}

/**
 * A DATE column ("2026-10-07") as its own day key. `new Date("2026-10-07")`
 * is UTC midnight, which is the evening before anywhere west of Greenwich, so
 * a board card due on the 7th would draw on the 6th. Timestamps go through
 * dayKey(new Date(ts)) instead.
 */
export function dateOnlyKey(value: string): string {
  return value.slice(0, 10);
}

/** "octubre de 2026" → "Octubre de 2026": first letter only (CSS capitalize would give "De"). */
export function upperFirst(s: string): string {
  return s.charAt(0).toLocaleUpperCase() + s.slice(1);
}

/** Weekday short names Monday-first, in the reader's language. */
export function weekdayLabels(locale: string): string[] {
  return Array.from({ length: 7 }, (_, i) =>
    new Date(2024, 0, 1 + i).toLocaleDateString(locale, { weekday: "short" }), // 2024-01-01 is a Monday
  );
}
